import React from 'react';

import { Box, Icon } from '@betterment/samba-react';
import {
  Button,
  CenteredWorkflowLayout,
  Flash,
  Flex,
  Heading,
  SectionLayout,
  Text,
  theme,
} from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import CashBackSplashImage from '../../../images/checking_cash_back_splash.png';
import Footer from '../../../Shared/components/Footer';
import { useFooter } from '../../../Shared/components/FooterProvider';
import { CASH_SUPPORT_EMAIL } from '../../../Shared/constants';

export const CheckingDisclosure = styled(Text)`
  color: ${theme.colors.bmtGrey50};
  font-size: ${theme.fontSizes[1]};
  line-height: ${theme.lineHeights[1]};
  & > a {
    text-decoration: underline;
    font-size: ${theme.fontSizes[1]};
    line-height: ${theme.lineHeights[1]};
  }
`;

interface Props {
  handleClick: () => void;
  buttonCopy: string;
  displayFlash: boolean;
}

interface PerkProps {
  icon: React.ReactElement<{}>;
  text: string;
}

const Perk: React.FC<React.PropsWithChildren<PerkProps>> = ({ icon, text }) => {
  return (
    <Box
      css={{
        marginBottom: 'space.compat-7',
      }}
    >
      <Flex flexDirection="row" alignItems="top">
        <Box
          css={{
            marginRight: 'space.compat-2',
          }}
        >
          {icon}
        </Box>
        <Text as="p">{text}</Text>
      </Flex>
    </Box>
  );
};

const NoFeeCheckingDetailsCopy: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <>
      <>
        <Perk
          icon={<Icon name="AtmCard" size="small" color="icon" />}
          text="Unlimited reimbursements on ATM fees and foreign transaction fees."
        />
        <Perk
          icon={<Icon name="DollarCircle" size="small" color="icon" />}
          text="No overdraft fees and no minimum balances."
        />
        <Perk
          icon={<Icon name="Card" size="small" color="icon" />}
          text="You pay with your Betterment Visa® Debit Card in stores or online."
        />
      </>
    </>
  );
};

const NoFeeChecking: React.FC<React.PropsWithChildren<Props>> = ({ handleClick, buttonCopy, displayFlash }) => {
  const { data: disclosureData } = useFooter();

  return (
    <CenteredWorkflowLayout title="No-fee checking">
      <CenteredWorkflowLayout.Slot name="content">
        <Box
          css={{
            marginBottom: 'space.300',
          }}
        >
          <Box
            css={{
              maxWidth: '60rem',
              margin: '0 auto',

              marginBottom: {
                base: 'space.200',
                sm: 'space.200',
                md: 'space.0',
              },
            }}
          >
            <img src={CashBackSplashImage} alt="No fee checking." width="100%" />
          </Box>
        </Box>
        <Heading color="bmtGrey90">All your checking needs, none of the fees.‡</Heading>
        <Text as="p">We believe accessing your money shouldn't cost you, no matter where you are:</Text>
        <Flex justifyContent="center">
          <Box
            css={{
              width: '100%',
            }}
          >
            <SectionLayout px="0">
              <Box>
                <NoFeeCheckingDetailsCopy />
              </Box>
            </SectionLayout>
          </Box>
        </Flex>
        <CenteredWorkflowLayout.Actions>
          {displayFlash ? (
            <Flash mb="9" variant="warning" icon={false}>
              <Flex m="2">
                <Box
                  css={{
                    marginRight: 'space.100',
                  }}
                >
                  <Icon name="ExclamationCircle" size="small" color="icon" />
                </Box>
                <Text as="p">
                  Looks like your account needs some attention. Please contact us at{' '}
                  <a href={`mailto:${CASH_SUPPORT_EMAIL}`}>{CASH_SUPPORT_EMAIL}</a> to resolve. Once that's done, you'll
                  be able to access cash back rewards.
                </Text>
              </Flex>
            </Flash>
          ) : (
            <Button mt="0" px="9" mb="9" onClick={handleClick}>
              {buttonCopy}
            </Button>
          )}
        </CenteredWorkflowLayout.Actions>
        {disclosureData && (
          <Box
            css={{
              marginBottom: 'space.300',
            }}
          >
            <Flex>
              <CheckingDisclosure
                as="p"
                dangerouslySetInnerHTML={{
                  __html: `‡ ${disclosureData.terms.checkingDisclosure}`,
                }}
              />
            </Flex>
          </Box>
        )}
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default NoFeeChecking;
